import type { Indicador } from '../../types/chamado'
import type { LinhaDaSerie } from '../../types/indicadores'

/** A ordem em que o Anexo VIII numera os seis. É a ordem das colunas, e não muda. */
export const ORDEM_DOS_INDICADORES: Indicador[] = ['ICA', 'IAP', 'IAE', 'IAD', 'TIS', 'IIE']

/**
 * As versões de cálculo presentes na série.
 *
 * Duas versões diferentes significam meses que **não se comparam diretamente**: a fórmula de algum
 * indicador mudou entre eles, e uma linha que suba de um mês para o outro pode ser a regra que
 * mudou, não o desempenho. A tela avisa quando isso acontece — em silêncio, a série mentiria.
 *
 * @param linhas a série inteira
 * @returns as versões distintas, em ordem
 */
export function versoesDeCalculo(linhas: LinhaDaSerie[]): string[] {
  const versoes = new Set<string>()
  for (const linha of linhas) {
    if (linha.versaoDoCalculo) {
      versoes.add(linha.versaoDoCalculo)
    }
  }
  return [...versoes].sort()
}

/**
 * O indicador de uma linha, pelo código.
 *
 * @param linha a competência
 * @param codigo qual dos seis
 * @returns o indicador daquela linha, ou nulo quando o congelado não pôde ser lido
 */
export function indicadorDe(linha: LinhaDaSerie, codigo: Indicador) {
  return linha.indicadores.find((i) => i.codigo === codigo) ?? null
}

/**
 * A altura da barra de um desconto, em porcentagem da altura disponível.
 *
 * O teto de 10% do contrato é o topo da escala: uma barra desenhada sobre o maior valor da série
 * faria um mês de 2% parecer alto num contrato bem executado e baixo num mal executado. A escala é
 * do contrato, não do dado.
 *
 * @param redutor pontos percentuais de desconto
 * @returns a altura em porcentagem, de 0 a 100
 */
export function alturaDaBarra(redutor: number): number {
  return Math.min(100, Math.round((redutor / TETO_DO_CONTRATO) * 100))
}

/** Teto de redução sobre a nota fiscal (Anexo VIII, item h, p. 148). */
export const TETO_DO_CONTRATO = 10
